import type {
  ShipmentHandover,
  ShipmentImportResult,
  ShipmentImportRow,
} from "~/types/admin-shipping";

export interface ImportBatch {
  batchId: string;
  fileName: string;
  rows: ShipmentImportRow[];
  rowCount: number;
}

export const unregisteredShipmentReferenceLabel =
  "未登録の対象（全期間）";

export type ShipmentHandoverDraft = Readonly<{
  recordHandover: boolean;
  handedOverOn: string;
  method: ShipmentHandover["method"];
  reportedCount: string;
  note: string;
}>;

export function createShipmentHandoverDraft(
  rowCount: number,
  handedOverOn: string,
): ShipmentHandoverDraft {
  return {
    recordHandover: true,
    handedOverOn,
    method: "counter",
    reportedCount: String(rowCount),
    note: "",
  };
}

export type ShipmentImportState =
  | Readonly<{ phase: "idle" }>
  | Readonly<{ phase: "previewing"; batch: ImportBatch }>
  | Readonly<{
      phase: "ready";
      batch: ImportBatch;
      preview: ShipmentImportResult;
    }>
  | Readonly<{
      phase: "committing";
      batch: ImportBatch;
      preview: ShipmentImportResult;
    }>
  | Readonly<{
      phase: "complete";
      batch: ImportBatch;
      preview: ShipmentImportResult;
      result: ShipmentImportResult;
    }>;

export type ShipmentImportAction =
  | Readonly<{ type: "reset" }>
  | Readonly<{ type: "file_parsed"; batch: ImportBatch }>
  | Readonly<{
      type: "preview_received";
      batchId: string;
      preview: ShipmentImportResult;
    }>
  | Readonly<{ type: "preview_failed"; batchId: string }>
  | Readonly<{ type: "commit_started" }>
  | Readonly<{ type: "commit_received"; result: ShipmentImportResult }>
  | Readonly<{ type: "commit_failed" }>;

export const initialShipmentImportState: ShipmentImportState = {
  phase: "idle",
};

export function shipmentImportReducer(
  state: ShipmentImportState,
  action: ShipmentImportAction,
): ShipmentImportState {
  switch (action.type) {
    case "reset":
      return initialShipmentImportState;
    case "file_parsed":
      return { phase: "previewing", batch: action.batch };
    case "preview_received":
      if (
        state.phase !== "previewing" ||
        state.batch.batchId !== action.batchId
      ) {
        return state;
      }
      return { phase: "ready", batch: state.batch, preview: action.preview };
    case "preview_failed":
      return state.phase === "previewing" &&
          state.batch.batchId === action.batchId
        ? initialShipmentImportState
        : state;
    case "commit_started":
      if (state.phase !== "ready" || !canCommitShipmentImport(state)) {
        return state;
      }
      return {
        phase: "committing",
        batch: state.batch,
        preview: state.preview,
      };
    case "commit_received":
      if (state.phase !== "committing") return state;
      if (action.result.result !== "committed") {
        return {
          phase: "ready",
          batch: state.batch,
          preview: action.result,
        };
      }
      return {
        phase: "complete",
        batch: state.batch,
        preview: state.preview,
        result: action.result,
      };
    case "commit_failed":
      if (state.phase !== "committing") return state;
      return { phase: "ready", batch: state.batch, preview: state.preview };
  }
}

export function canCommitShipmentImport(
  state: ShipmentImportState,
): state is Extract<ShipmentImportState, { phase: "ready" }> {
  return (
    state.phase === "ready" &&
    state.batch.rowCount > 0 &&
    state.preview.result === "dry_run" &&
    state.preview.error_count === 0
  );
}
